import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, getToken } from "../api";
import { LANDING_SEGMENTS, PROMO_MEDIUMS } from "../../site/landing/content";

/* 랜딩페이지(/for/*) 상담 문의 — 세그먼트 · 채널 · 영업 단계별로 본다 */
const STATUSES = [
  ["NEW", "신규"],
  ["REVIEW", "검토"],
  ["MEETING", "미팅"],
  ["CONTRACT", "계약"],
  ["CLOSED", "종료"],
];
const STATUS_LABEL = Object.fromEntries(STATUSES);
const SEGMENT_LABEL = Object.fromEntries(LANDING_SEGMENTS.map((s) => [s.slug, s.label]));
const MEDIUM_LABEL = Object.fromEntries(PROMO_MEDIUMS);
const OPTION_LABEL = { A: "A 공개 제작형", B: "B 제품화 파트너형", C: "C 일반 개발형", "?": "잘 모르겠음" };

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(/[Zz+]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ko-KR", { timeZone: "Asia/Seoul", dateStyle: "medium", timeStyle: "short" });
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: "var(--s-fg-mute)", marginBottom: 3 }}>{label}</div>
      <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{children || "—"}</div>
    </div>
  );
}

function channelText(item) {
  if (!item.source) return "direct";
  return item.medium ? `${item.source} · ${MEDIUM_LABEL[item.medium] || item.medium}` : item.source;
}

function Row({ item, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const [memo, setMemo] = useState(item.admin_memo || "");
  const [busy, setBusy] = useState(false);

  async function patch(body) {
    setBusy(true);
    try { await onChange(item.id, body); } finally { setBusy(false); }
  }

  async function downloadAttachment() {
    setBusy(true);
    try {
      const res = await fetch(`/api/sstoa/landing/leads/${item.id}/attachment`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("첨부를 받지 못했습니다.");
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = item.attachment_name || `lead-${item.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusy(false);
    }
  }

  const closed = item.status === "CLOSED";

  return (
    <>
      <tr style={{ cursor: "pointer" }} onClick={() => setOpen((v) => !v)}>
        <td>
          <span className={`sstoa-tag ${closed ? "sstoa-tag--gray" : ""}`}>{STATUS_LABEL[item.status] || item.status}</span>
        </td>
        <td style={{ fontWeight: 600 }}>
          {item.name}
          <div style={{ fontWeight: 400, fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>{item.phone}</div>
        </td>
        <td>{SEGMENT_LABEL[item.segment] || item.segment || "—"}</td>
        <td>{channelText(item)}</td>
        <td>{OPTION_LABEL[item.desired_option] || "—"}</td>
        <td>{item.stage || "—"}</td>
        <td>{formatWhen(item.created_at)}</td>
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          {item.attachment_name ? "📎 " : ""}
          {!item.mail_sent && <span className="sstoa-tag sstoa-tag--gray" title="관리자 알림 메일이 발송되지 않았습니다">메일 실패</span>}
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={8} style={{ background: "rgba(255,255,255,0.02)" }}>
            <div style={{ padding: "6px 2px 14px" }}>
              <div className="sstoa-meta" style={{ marginBottom: 14 }}>
                <span>L-{item.id}</span>
                <span>{item.email}</span>
                <span>개인정보 동의 {formatWhen(item.privacy_agreed_at)}</span>
                <span>확인메일 {item.ack_sent ? "발송" : "미발송"}</span>
              </div>

              <Field label="아이디어 요약">{item.idea}</Field>
              <div className="sstoa-meta" style={{ marginBottom: 14 }}>
                <span>예산 {item.budget || "—"}</span>
                <span>희망 완료 {item.due || "—"}</span>
                <span>예상 수량 {item.quantity || "—"}</span>
                <span>지원사업 {item.gov_support || "—"}</span>
                <span>영상 공개 {item.video_ok || "—"}</span>
              </div>
              <div className="sstoa-meta" style={{ marginBottom: 14 }}>
                <span>유입 {item.source || "direct"}</span>
                {item.medium && <span>형태 {MEDIUM_LABEL[item.medium] || item.medium}</span>}
                {item.campaign && <span>캠페인 {item.campaign}</span>}
                {item.content && <span>링크 {item.content}</span>}
                {item.referrer && <span title={item.referrer}>리퍼러 {item.referrer.slice(0, 60)}</span>}
              </div>

              {item.attachment_name && (
                <div style={{ marginBottom: 14 }}>
                  <button className="sstoa-btn sstoa-btn--sm" onClick={downloadAttachment} disabled={busy}>
                    첨부 내려받기 — {item.attachment_name} {formatSize(item.attachment_size)}
                  </button>
                </div>
              )}

              <div className="sstoa-field" style={{ marginBottom: 12 }}>
                <label>검토 메모</label>
                <textarea className="sstoa-textarea" value={memo} disabled={busy} onChange={(e) => setMemo(e.target.value)} />
                <div className="sstoa-actions" style={{ marginTop: 8 }}>
                  <button className="sstoa-btn sstoa-btn--sm" onClick={() => patch({ admin_memo: memo })} disabled={busy}>메모 저장</button>
                </div>
              </div>

              <div className="sstoa-actions">
                <a className="sstoa-btn sstoa-btn--sm" href={`mailto:${item.email}?subject=${encodeURIComponent(`[I.META] 아이디어 상담 문의 회신 (L-${item.id})`)}`}>
                  메일로 회신
                </a>
                <a className="sstoa-btn sstoa-btn--sm" href={`tel:${item.phone.replace(/[^0-9+]/g, "")}`}>전화</a>
                {STATUSES.map(([v, l]) => (
                  <button
                    key={v} disabled={busy || item.status === v}
                    className={`sstoa-btn sstoa-btn--sm${item.status === v ? " sstoa-btn--primary" : ""}`}
                    onClick={() => patch({ status: v })}
                  >
                    {l}
                  </button>
                ))}
                <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => onRemove(item.id)} disabled={busy}>삭제</button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function LeadsPage() {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState(null);
  const [status, setStatus] = useState("");
  const [segment, setSegment] = useState("");
  const [source, setSource] = useState("");
  const [error, setError] = useState("");

  async function load(next = {}) {
    const q = { status, segment, source, ...next };
    const qs = new URLSearchParams(Object.entries(q).filter(([, v]) => v)).toString();
    setError("");
    try {
      const [list, c] = await Promise.all([
        api.get(`/landing/leads${qs ? `?${qs}` : ""}`),
        api.get("/landing/leads/counts"),
      ]);
      setRows(list);
      setCounts(c);
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onFilter(key, value) {
    if (key === "status") setStatus(value);
    if (key === "segment") setSegment(value);
    if (key === "source") setSource(value);
    load({ [key]: value });
  }

  /* 채널 선택지는 지금까지 들어온 문의에서 뽑는다 (필터 중이어도 목록이 줄지 않게 처음 값 유지) */
  const [knownSources, setKnownSources] = useState([]);
  const sourcesNow = useMemo(() => (rows || []).map((r) => r.source || "").filter(Boolean), [rows]);
  useEffect(() => {
    setKnownSources((prev) => Array.from(new Set([...prev, ...sourcesNow])).sort());
  }, [sourcesNow]);

  async function change(id, patch) {
    try {
      await api.post(`/landing/leads/${id}`, patch);
      await load();
    } catch (err) { setError(err.message); }
  }

  async function remove(id) {
    if (!window.confirm("이 문의를 삭제할까요? 첨부 파일도 함께 지워집니다.")) return;
    try {
      await api.del(`/landing/leads/${id}`);
      await load();
    } catch (err) { setError(err.message); }
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/sstoa" className="sstoa-back">← 홈</Link>
      </div>

      <div className="sstoa-topbar">
        <div>
          <h1 className="sstoa-title">랜딩 문의</h1>
          <p className="sstoa-sub">
            분야별 랜딩(/for/*)의 공통 문의로 접수된 상담 요청입니다. 행을 누르면 내용이 펼쳐집니다.{" "}
            <Link to="/sstoa/landing">랜딩페이지 관리 (짧은 링크 · 유입 통계) →</Link>
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <button className={`sstoa-btn sstoa-btn--sm${status === "" ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter("status", "")}>
          전체{counts ? ` ${counts.total}` : ""}
        </button>
        {STATUSES.map(([v, l]) => (
          <button key={v} className={`sstoa-btn sstoa-btn--sm${status === v ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter("status", v)}>
            {l}{counts ? ` ${counts[v] || 0}` : ""}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <select className="sstoa-input" style={{ width: "auto" }} value={segment} onChange={(e) => onFilter("segment", e.target.value)}>
          <option value="">모든 분야</option>
          {LANDING_SEGMENTS.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
        </select>
        <select className="sstoa-input" style={{ width: "auto" }} value={source} onChange={(e) => onFilter("source", e.target.value)}>
          <option value="">모든 채널</option>
          {knownSources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="sstoa-empty">접수된 문의가 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead>
              <tr>
                <th>단계</th><th>문의자</th><th>분야</th><th>유입 채널</th><th>희망 옵션</th><th>현재 단계</th><th>접수</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((it) => <Row key={it.id} item={it} onChange={change} onRemove={remove} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
